import { useEffect, useState } from 'react';
import axios from 'axios';
import { ClipboardCheck, Eye, Inbox, Search, ShieldAlert, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { WORK_PERMIT_TYPES, formatDate } from '../components/work-permits/constants';
import { StoreStatusBadge } from '../components/stores/incoming/StoreStatusBadge';
import { StorePermitDetail, STORE_WARNING } from '../components/stores/incoming/StorePermitDetail';
import { ObjectionDialog } from '../components/stores/incoming/ObjectionDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEADS = ['Talep No', 'Durum', 'Lokasyon', 'İş Tipi / İş Çeşidi', 'Firma', 'İş İzinleri', 'Personel', 'Tarih / Saat', 'İşlemler'];

const PermitRow = ({ p, onOpen, onObject }) => (
  <TableRow className="table-row-hover" data-testid={`incoming-permit-${p.id}`}>
    <TableCell className="text-center"><span className="rounded bg-slate-100 px-2 py-1 font-mono text-xs">{p.request_number || '-'}</span></TableCell>
    <TableCell><div className="flex flex-col items-center gap-1"><StoreStatusBadge permit={p} />{p.store_objection && <Badge className="bg-rose-50 text-rose-700 hover:bg-rose-50 text-[11px]" data-testid={`incoming-objection-${p.id}`}>{p.store_objection.resolution ? (p.store_objection.resolution.action === 'continue' ? 'İtiraz: talep devam ediyor' : 'İtiraz: talep iptal edildi') : 'İtiraz iletildi'}</Badge>}</div></TableCell>
    <TableCell className="text-center">{p.location_name}</TableCell>
    <TableCell className="text-center"><Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{[p.is_tipi, p.is_cesidi].filter(Boolean).join(' / ')}</Badge></TableCell>
    <TableCell className="text-center font-medium">{p.company_name}{p.on_behalf_of && <p className="text-xs font-normal text-sky-700">{p.on_behalf_of} adına</p>}</TableCell>
    <TableCell className="text-center">
      {p.work_permit_types?.length ? <div className="flex flex-wrap justify-center gap-1">{p.work_permit_types.map(t => <Badge key={t} variant="outline" className="border-amber-200 bg-amber-50 text-xs text-amber-700">{WORK_PERMIT_TYPES.find(x => x.id === t)?.label || t}</Badge>)}</div> : <span className="text-xs text-slate-400">-</span>}
    </TableCell>
    <TableCell className="text-center"><span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700"><Users className="h-3 w-3" />{p.employees.length} personel</span></TableCell>
    <TableCell className="text-center text-sm whitespace-nowrap">{formatDate(p.start_date)} {p.start_time}<div className="text-xs text-slate-400">{formatDate(p.end_date)} {p.end_time}</div></TableCell>
    <TableCell>
      <div className="flex items-center justify-center gap-1">
        <Button size="sm" variant="ghost" onClick={() => onOpen(p)} title="Detay" data-testid={`incoming-open-${p.id}`}><Eye className="h-4 w-4" /></Button>
        {!p.store_objection && <Button size="sm" variant="outline" className="h-8 border-rose-300 text-xs text-rose-700 hover:bg-rose-50" onClick={() => onObject(p)} data-testid={`incoming-object-${p.id}`}>Bilgim dahilinde değil</Button>}
      </div>
    </TableCell>
  </TableRow>
);

export default function IncomingPermitsPage() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const [objecting, setObjecting] = useState(null);
  useEffect(() => { axios.get(`${API}/firm/store-incoming-permits`).then(r => setRows(r.data)).catch(() => setRows([])); }, []);
  const s = q.trim().toLocaleLowerCase('tr');
  const shown = (rows || []).filter(p => !s || [p.request_number, p.company_name, p.on_behalf_of, p.job_description].some(v => (v || '').toLocaleLowerCase('tr').includes(s)));
  const objected = (id, obj) => {
    setRows(rs => rs.map(r => (r.id === id ? { ...r, store_objection: obj } : r)));
    setDetail(d => (d?.id === id ? { ...d, store_objection: obj } : d));
    setObjecting(null);
  };
  return (
    <div className="space-y-6" data-testid="incoming-permits-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><Inbox className="h-6 w-6" />Gelen Çalışma Talepleri</h1>
        <p className="text-sm text-slate-500">Mağazanızda/işletmenizde çalışma yapmak için oluşturulan talepler ve güncel durumları</p>
      </div>
      <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="incoming-permits-note">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />{STORE_WARNING}
      </div>
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Talep no, firma veya iş ara..." className="pl-9" data-testid="incoming-permits-search" />
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">{HEADS.map(h => <TableHead key={h} className="text-center font-semibold">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {rows === null || shown.length === 0 ? (
                <TableRow><TableCell colSpan={HEADS.length} className="py-12 text-center">
                  <div className="flex flex-col items-center gap-2" data-testid="incoming-permits-empty"><ClipboardCheck className="h-8 w-8 text-slate-300" /><p className="text-slate-500">{rows === null ? 'Yükleniyor...' : 'Henüz gelen çalışma talebi yok'}</p></div>
                </TableCell></TableRow>
              ) : shown.map(p => <PermitRow key={p.id} p={p} onOpen={setDetail} onObject={setObjecting} />)}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <StorePermitDetail permit={detail} onClose={() => setDetail(null)} onObject={setObjecting} />
      <ObjectionDialog permit={objecting} onClose={() => setObjecting(null)} onDone={objected} />
    </div>
  );
}
